import React, { useCallback, useEffect, useState } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBIcon,
  MDBCard,
  MDBCardBody,
  MDBCollapse,
  MDBSpinner,
} from 'mdbreact';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import api from '../../../services/api';
import Loading from '../../../components/Loading';
import NavBar from '../../../components/NavBar';

// ADMINISTRACAO > Rotinas automáticas
//
// A tela NAO conhece rotina nenhuma pelo nome. Tudo que ela sabe sobre como
// mostrar o resultado vem do `formatoResultado` que a API devolve junto de
// cada rotina — e' isso que permite a sexta rotina entrar no registro do
// backend sem tocar em nada aqui.

// Tabela de linhas de um resultado — usada pelo `tipo: 'lista'` e pelo
// `detalhe` de um resultado de contagem/texto. Os dois renderizam a mesma
// coisa; o que muda e de onde vem a lista.
function TabelaDeLinhas({ colunas, linhas }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table table-sm mb-0">
        <thead>
          <tr>
            {colunas.map(c => (
              <th key={c.chave}>{c.titulo}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={i}>
              {colunas.map(c => (
                <td key={c.chave}>{String(l[c.chave] ?? '')}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// O MANIFESTO de uma execucao destrutiva, escondido atras de um clique.
//
// Por que fechado por padrao: a limpeza de acessos apaga ~120 linhas por
// execucao, e uma tabela dessas dentro do card empurraria todo o resto da tela
// para fora. Por que existe: este branch inteiro nasceu de acesso apagado sem
// deixar rastro (#314) — o numero "121 acessos removidos" nao diz de quem eram
// nem permite desfazer. `<details>` e HTML puro, sem estado nem dependencia.
// Teto de linhas que o detalhe renderiza. O conteudo de um <details> entra no
// DOM mesmo fechado, e este componente aparece no card E em cada uma das ate 50
// linhas do historico — entao um manifesto grande multiplica por 51. A primeira
// execucao da limpeza em producao alcanca todo o historico de uma vez e pode
// trazer milhares de linhas; sem teto, abrir a tela montaria dezenas de milhares
// de <tr> em React 16 e travaria a aba. A lista COMPLETA nunca esta so aqui:
// ela fica no JSONB de `rotina_execucao.resultado`, que e' de onde a
// restauracao le (o SELECT esta no corpo do PR do agendador).
const MAX_LINHAS_DETALHE = 200;

function Detalhe({ detalhe, valor }) {
  if (!detalhe || !Array.isArray(detalhe.colunas) || !detalhe.colunas.length) return null;
  const linhas = valor[detalhe.chave];
  if (!Array.isArray(linhas) || !linhas.length) return null;
  const mostradas = linhas.slice(0, MAX_LINHAS_DETALHE);
  const ocultas = linhas.length - mostradas.length;
  return (
    <details style={{ fontSize: '12px' }} className="mt-1">
      <summary style={{ cursor: 'pointer', color: '#1976d2' }}>
        {detalhe.titulo || 'detalhe'} ({linhas.length})
      </summary>
      <div className="mt-1">
        <TabelaDeLinhas colunas={detalhe.colunas} linhas={mostradas} />
        {ocultas > 0 && (
          <p className="grey-text mb-0 mt-1">
            Mostrando as primeiras {MAX_LINHAS_DETALHE} de {linhas.length} linhas. A lista
            completa fica gravada em <code>rotina_execucao.resultado</code> e e&apos; de la
            que a restauracao le.
          </p>
        )}
      </div>
    </details>
  );
}

// Renderiza o resultado a partir do formato DECLARADO pela rotina. Sem isto, a
// tela viraria um switch por nome de rotina, e acrescentar a sexta exigiria
// mexer aqui — matando a escalabilidade que o desenho promete.
//
// So o que esta DECLARADO aparece: a chave destacada e, se houver, as colunas
// do `detalhe`. Chave do `resultado` que nao esta em nenhum dos dois fica
// gravada no JSONB e nao chega a tela (e o caso de `usuariosAvaliados`) — quem
// for depurar le a coluna `resultado` da tabela `rotina_execucao`.
function Resultado({ formato, valor }) {
  if (!formato || !valor) return <span className="grey-text">—</span>;

  if (formato.tipo === 'contagem') {
    return (
      <span>
        <strong>{valor[formato.chave]}</strong> {formato.rotulo}
        <Detalhe detalhe={formato.detalhe} valor={valor} />
      </span>
    );
  }

  if (formato.tipo === 'texto') {
    return (
      <span>
        {valor[formato.chave]}
        <Detalhe detalhe={formato.detalhe} valor={valor} />
      </span>
    );
  }

  if (formato.tipo === 'lista') {
    // Defesa que vale por si so, independente da validacao do registro no
    // backend (virgem/src/app/rotinas/registro.js): a tela renderiza o que a
    // API mandar, venha de onde vier. Sem isto, `formato.colunas.map(...)`
    // lanca `TypeError` para um `formatoResultado` mal formado — e como o
    // SEGOV nao tem `ErrorBoundary` em lugar nenhum, isso nao quebra so esta
    // linha, branqueia a TELA INTEIRA (a mesma tela que existe para mostrar o
    // que esta falhando). Degrada igual ao `tipo` desconhecido, sem lancar.
    if (!Array.isArray(formato.colunas) || !formato.colunas.length) {
      return <span className="grey-text">formato desconhecido (lista sem colunas)</span>;
    }
    const linhas = valor[formato.chave] || [];
    if (!linhas.length) return <span className="grey-text">nenhum registro</span>;
    return <TabelaDeLinhas colunas={formato.colunas} linhas={linhas} />;
  }

  return <span className="grey-text">formato desconhecido ({formato.tipo})</span>;
}

const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

const quandoEmTexto = q => {
  if (!q) return 'indefinido';
  const h = `${String(q.hora ?? 3).padStart(2, '0')}h`;
  if (q.tipo === 'diaria') return `todo dia às ${h}`;
  if (q.tipo === 'semanal') return `toda ${DIAS[q.diaSemana]} às ${h}`;
  if (q.tipo === 'mensal') return `todo dia ${q.dia} às ${h}`;
  if (q.tipo === 'meses') return `dia ${q.dia} dos meses ${q.meses.join(', ')} às ${h}`;
  return 'indefinido';
};

const dataHora = d => (d ? format(new Date(d), 'dd/MM/yyyy HH:mm') : '—');

// Duracao de uma execucao TERMINADA — a spec pede na tela ("ultima execucao,
// status, duracao e proxima prevista").
//
// Calculada dos dois timestamps, e nao do `_segundos` do resultado: aquele
// campo so e gravado quando a execucao ESTOURA o teto (ver agendador.js), entao
// usa-lo daria duracao em branco para toda execucao normal. Execucao em curso
// nao tem duracao: quanto ela ja leva aparece na situacao ("há N min").
const duracao = e => {
  if (!e || !e.iniciou_em || !e.terminou_em) return '—';
  const ms = new Date(e.terminou_em).getTime() - new Date(e.iniciou_em).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const segundos = Math.round(ms / 1000);
  if (segundos < 60) return `${segundos}s`;
  return `${Math.floor(segundos / 60)}min ${String(segundos % 60).padStart(2, '0')}s`;
};

// Quem disparou. A spec pede "agendador ou usuario_id" — o nome vem do
// `include` do RotinaController; sem ele a tela mostrava so "manual", que nao
// responde a pergunta que alguem faz ao ver uma rotina destrutiva executada
// fora de hora.
const quemDisparou = e => {
  if (!e || !e.disparado_por) return '—';
  if (e.disparado_por !== 'manual') return e.disparado_por;
  const u = e.usuario;
  const nome = u ? `${u.nome || ''} ${u.sobrenome || ''}`.trim() : '';
  return nome ? `manual (${nome})` : 'manual';
};

// Quanto tempo uma execucao 'rodando' ja leva. Mantido visivel mesmo depois do
// destaque por teto entrar: o numero cru continua util (ex.: "ha 3 min" perto
// do teto de 120s ainda e' informativo mesmo sem estourar).
const decorridoDesde = iso => {
  if (!iso) return '';
  const minutos = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return minutos <= 1 ? 'há menos de 1 min' : `há ${minutos} min`;
};

// `rodando` ha mais tempo que o teto DECLARADO da rotina e' o "problema" que a
// spec pede para destacar — nao qualquer `rodando`. Uma execucao em curso
// dentro do teto e' normal (inclusive: como o disparo manual prende a propria
// aba, um `rodando` visto por OUTRA aba antes do teto estourar costuma ser so
// outra pessoa/tick executando ao mesmo tempo, nao uma orfa).
const passouDoTeto = (execucao, tetoSegundos) => {
  if (!execucao || execucao.status !== 'rodando') return false;
  if (!Number.isFinite(tetoSegundos)) return false; // sem teto conhecido, sem julgamento
  const decorridoMs = Date.now() - new Date(execucao.iniciou_em).getTime();
  return decorridoMs > tetoSegundos * 1000;
};

const ROTULO_STATUS = {
  ok: 'concluída',
  falhou: 'falhou',
  rodando: 'em execução',
};

// Cor da situacao: vermelho para o que precisa de atencao de verdade (falhou,
// ou rodando ALEM do teto declarado — nao qualquer `rodando`). O estouro de
// teto com status 'ok' e' um caso diferente e NAO entra aqui: nao e' falha,
// e' so os dois relogios do agendador divergindo (ver comentario em
// virgem/src/app/services/agendador.js).
function CorSituacao({ execucao, tetoSegundos }) {
  if (!execucao) return <span className="grey-text">nunca executou</span>;
  const rotulo = ROTULO_STATUS[execucao.status] || execucao.status;

  if (execucao.status === 'falhou') {
    return <span className="red-text font-weight-bold">{rotulo}</span>;
  }
  if (execucao.status === 'rodando') {
    if (passouDoTeto(execucao, tetoSegundos)) {
      return (
        <span className="red-text font-weight-bold">
          <MDBIcon icon="exclamation-triangle" className="mr-1" />
          rodando além do teto ({decorridoDesde(execucao.iniciou_em)}, teto {tetoSegundos}s)
        </span>
      );
    }
    return (
      <span className="text-info font-weight-bold">
        {rotulo} ({decorridoDesde(execucao.iniciou_em)})
      </span>
    );
  }
  if (execucao.resultado && execucao.resultado._estourouTeto) {
    return (
      <span className="orange-text text-darken-2 font-weight-bold">
        {rotulo} (excedeu o teto de tempo, {execucao.resultado._segundos}s)
      </span>
    );
  }
  return <span className="green-text text-darken-2">{rotulo}</span>;
}

export default function Rotinas() {
  const [rotinas, setRotinas] = useState([]);
  // `null` = ainda nao sabemos (carregando). So o `false` explicito vindo da API
  // acende o aviso — nao piscar "desligado" antes da primeira resposta.
  const [agendadorLigado, setAgendadorLigado] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [executando, setExecutando] = useState(null);

  // historico: accordion (uma rotina aberta por vez), com cache por nome —
  // nao busca de novo so por fechar/reabrir a mesma.
  const [historicoAberto, setHistoricoAberto] = useState(null);
  const [historicos, setHistoricos] = useState({}); // { [nome]: 'carregando' | array }

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get('/rotinas');
      const dados = r.data || {};
      setRotinas(Array.isArray(dados.rotinas) ? dados.rotinas : []);
      setAgendadorLigado(
        typeof dados.agendadorLigado === 'boolean' ? dados.agendadorLigado : null
      );
    } catch (error) {
      // o toast de erro vem do interceptor de services/api.js
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Busca o historico de uma rotina e guarda em cache. Usada tanto ao abrir o
  // painel quanto ao recarrega-lo depois de uma execucao.
  const buscaHistorico = useCallback(async nome => {
    setHistoricos(prev => ({ ...prev, [nome]: 'carregando' }));
    try {
      const r = await api.get(`/rotinas/${nome}/execucoes`);
      setHistoricos(prev => ({ ...prev, [nome]: Array.isArray(r.data) ? r.data : [] }));
    } catch (error) {
      // o toast de erro vem do interceptor; aqui so libera o cache para poder
      // tentar de novo ao reabrir
      setHistoricos(prev => {
        const { [nome]: _descarta, ...resto } = prev;
        return resto;
      });
    }
  }, []);

  const toggleHistorico = async nome => {
    if (historicoAberto === nome) {
      setHistoricoAberto(null);
      return;
    }
    setHistoricoAberto(nome);
    if (historicos[nome]) return; // ja em cache (ou ja carregando)
    await buscaHistorico(nome);
  };

  // A trava do backend impede clique SIMULTANEO (409 do advisory lock), nao
  // clique SEGUIDO. Para rotina nao-repetivel, clicar de novo DEPOIS que a
  // primeira ja terminou nao e' barrado pelo servidor — e e' exatamente o que
  // causa dano (ex.: congelar o quadrimestre duas vezes). Por isso a
  // confirmacao mora aqui, mostrando quando rodou por ultima vez, e nao so no
  // servidor. `repetivel` ausente (undefined) e' tratado como NAO repetivel —
  // o lado seguro, igual o backend faz em `RotinaController.executar`.
  //
  // `destrutiva` entra na mesma condicao, e as duas perguntas sao diferentes:
  // "rodar duas vezes piora?" e "apaga dado?". A limpeza de acessos responde
  // NAO e SIM — pela condicao antiga, a unica rotina que apaga linha de
  // `acesso` era justamente a unica que nao pedia confirmacao nenhuma. O
  // backend repete a checagem (`RotinaController.executar`): a confirmacao da
  // tela e conveniencia, nao protecao.
  const disparar = async rotina => {
    if (!rotina.repetivel || rotina.destrutiva) {
      const ultima = rotina.ultima ? dataHora(rotina.ultima.iniciou_em) : 'nunca';
      const motivo = rotina.destrutiva
        ? 'APAGA dados de forma definitiva'
        : 'não pode ser repetida sem risco de dano';
      // eslint-disable-next-line no-alert
      const segue = window.confirm(
        `"${rotina.descricao}" ${motivo}.\n` +
          `Última execução: ${ultima}.\n\nExecutar mesmo assim?`
      );
      if (!segue) return;
    }

    setExecutando(rotina.nome);
    // A requisicao fica pendurada ate a rotina terminar (ate o teto dela, hoje
    // 120s) — e' assim por design (proxy_read_timeout 600s no nginx, sem
    // timeout no axios). Sem este aviso, quem clica e' deixado sem nenhum sinal
    // de que algo esta acontecendo por ate 2 minutos.
    toast.info(`Executando "${rotina.descricao}"... pode levar até 2 minutos.`);
    try {
      const r = await api.post(`/rotinas/${rotina.nome}/executar`, { confirmado: true });
      if (r.data && r.data.ok) toast.success(r.data.ok);
      // O historico desta rotina acabou de ganhar uma linha. Se o painel estiver
      // ABERTO, recarrega: apenas limpar o cache deixaria o painel aberto caindo
      // no estado vazio ("Sem execucoes registradas") logo depois de uma
      // execucao bem-sucedida, porque `toggleHistorico` so busca ao clicar.
      // Fechado, basta invalidar — a proxima abertura busca.
      if (historicoAberto === rotina.nome) {
        await buscaHistorico(rotina.nome);
      } else {
        setHistoricos(prev => {
          const { [rotina.nome]: _descarta, ...resto } = prev;
          return resto;
        });
      }
      await carregar();
    } catch (error) {
      // a mensagem do servidor (chave `erro` — inclusive o 409 do advisory
      // lock e o 409 de confirmacao) vem do interceptor de services/api.js
    } finally {
      setExecutando(null);
    }
  };

  // Rotina que falhou, ou que esta 'rodando' ha mais tempo que o proprio teto,
  // precisa saltar aos olhos de quem abre a tela — nao esperar que alguem va
  // procurar linha por linha. `rodando` DENTRO do teto nao entra aqui: e' so
  // execucao em curso normal.
  const emAlerta = r =>
    r.ultima &&
    (r.ultima.status === 'falhou' || passouDoTeto(r.ultima, r.tetoSegundos));

  return (
    <NavBar>
      <MDBContainer>
        <MDBRow className="h4-responsive mt-3" center>
          Rotinas automáticas
        </MDBRow>
        {agendadorLigado === false && (
          // A protecao do opt-in (`AGENDADOR_ENABLED`, ver agendador.js) nao
          // cobria ESTA tela, que existe em toda maquina que sobe a imagem.
          // Sem o aviso, quem abre ve "nunca executou" e um botao verde, e a
          // leitura natural e "a rotina esta atrasada, deixa eu disparar" — o
          // clique de teste que o opt-in existe para evitar.
          <MDBRow center className="mb-2">
            <MDBCol size="12">
              <div
                className="p-2 text-center"
                style={{ border: '1px solid #f0ad4e', background: '#fff8e1', fontSize: '13px' }}
              >
                <MDBIcon icon="exclamation-triangle" className="orange-text mr-2" />
                O agendador automático está <strong>DESLIGADO</strong> nesta instalação
                (<code>AGENDADOR_ENABLED</code>). Nada roda sozinho: as rotinas abaixo só
                executam por este botão. Ligar exige mudar a variável e reiniciar o container.
              </div>
            </MDBCol>
          </MDBRow>
        )}

        <MDBRow center className="mb-2">
          <MDBBtn size="sm" color="mdb-color" onClick={carregar} disabled={carregando}>
            <MDBIcon icon="sync" className="mr-2" />
            {carregando ? 'Carregando...' : 'Atualizar'}
          </MDBBtn>
        </MDBRow>

        {carregando ? (
          <Loading />
        ) : rotinas.length === 0 ? (
          <MDBRow center className="mt-3 text-danger font-weight-bolder">
            Nenhuma rotina registrada.
          </MDBRow>
        ) : (
          rotinas.map(r => (
            <MDBCard
              className="my-2"
              key={r.nome}
              style={emAlerta(r) ? { border: '2px solid #c0392b' } : undefined}
            >
              <MDBCardBody>
                <MDBRow className="align-items-center">
                  <MDBCol size="12" md="4">
                    <div className="font-weight-bold">
                      {emAlerta(r) && (
                        <MDBIcon icon="exclamation-triangle" className="red-text mr-1" />
                      )}
                      {r.descricao}
                    </div>
                    <div className="grey-text" style={{ fontSize: '12px' }}>
                      {quandoEmTexto(r.quando)}
                      {!r.repetivel && (
                        <span className="ml-2 orange-text text-darken-2">
                          (não repetível)
                        </span>
                      )}
                      {r.destrutiva && (
                        <span className="ml-2 red-text">(apaga dados)</span>
                      )}
                    </div>
                    <div className="grey-text" style={{ fontSize: '12px' }}>
                      {/* A PROXIMA prevista, calculada no backend. Com o
                          agendador desligado ela e' so a previsao teorica — o
                          aviso no topo da tela diz que nao vai acontecer. */}
                      Próxima prevista: {dataHora(r.proximaPrevista)}
                    </div>
                  </MDBCol>

                  <MDBCol size="12" md="3">
                    <div style={{ fontSize: '13px' }}>
                      Última: <strong>{dataHora(r.ultima && r.ultima.iniciou_em)}</strong>
                    </div>
                    <div style={{ fontSize: '13px' }}>
                      Situação:{' '}
                      <CorSituacao execucao={r.ultima} tetoSegundos={r.tetoSegundos} />
                    </div>
                    <div style={{ fontSize: '13px' }}>
                      Duração: {duracao(r.ultima)} · {quemDisparou(r.ultima)}
                    </div>
                    {r.ultima && r.ultima.status === 'falhou' && r.ultima.erro && (
                      <div className="red-text" style={{ fontSize: '12px' }}>
                        {r.ultima.erro}
                      </div>
                    )}
                  </MDBCol>

                  <MDBCol size="12" md="3">
                    <Resultado formato={r.formatoResultado} valor={r.ultima && r.ultima.resultado} />
                  </MDBCol>

                  <MDBCol size="12" md="2" className="text-md-right">
                    <MDBBtn
                      size="sm"
                      color="success"
                      disabled={executando === r.nome}
                      onClick={() => disparar(r)}
                    >
                      {executando === r.nome ? (
                        <>
                          <MDBSpinner small className="mr-2" />
                          Executando
                        </>
                      ) : (
                        <>
                          <MDBIcon icon="play" className="mr-2" />
                          Executar agora
                        </>
                      )}
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>

                <MDBRow className="mt-2">
                  <MDBCol size="12">
                    <button
                      type="button"
                      className="btn-link p-0 border-0 bg-transparent"
                      style={{ fontSize: '12px', color: '#1976d2', cursor: 'pointer' }}
                      onClick={() => toggleHistorico(r.nome)}
                    >
                      <MDBIcon icon="history" className="mr-1" />
                      {historicoAberto === r.nome ? 'Ocultar histórico' : 'Ver histórico'}
                    </button>

                    <MDBCollapse id={`hist-${r.nome}`} isOpen={historicoAberto === r.nome}>
                      <div className="mt-2" style={{ overflowX: 'auto' }}>
                        {historicos[r.nome] === 'carregando' ? (
                          <Loading />
                        ) : !historicos[r.nome] || historicos[r.nome].length === 0 ? (
                          <p className="grey-text" style={{ fontSize: '13px' }}>
                            Sem execuções registradas.
                          </p>
                        ) : (
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Início</th>
                                <th>Fim</th>
                                <th>Duração</th>
                                <th>Disparado por</th>
                                <th>Situação</th>
                                <th>Resultado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {historicos[r.nome].map(e => (
                                <tr key={e.id}>
                                  <td>{dataHora(e.iniciou_em)}</td>
                                  <td>{dataHora(e.terminou_em)}</td>
                                  <td>{duracao(e)}</td>
                                  <td>{quemDisparou(e)}</td>
                                  <td>
                                    <CorSituacao execucao={e} tetoSegundos={r.tetoSegundos} />
                                    {e.status === 'falhou' && e.erro && (
                                      <div className="red-text" style={{ fontSize: '11px' }}>
                                        {e.erro}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <Resultado formato={r.formatoResultado} valor={e.resultado} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </MDBCollapse>
                  </MDBCol>
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          ))
        )}
      </MDBContainer>
    </NavBar>
  );
}
